import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { getServiceBySlugs } from '../utils/services';

const SERVICE_AREA = ['Widnes', 'Runcorn', 'Warrington', 'St Helens', 'Liverpool'];

const ServiceTreatment = () => {
  const { categoryId, serviceSlug } = useParams();
  const serviceData = getServiceBySlugs(categoryId, serviceSlug);

  if (!serviceData) {
    return (
      <section className="services category-page">
        <div className="services-container">
          <h1 className="page-title">Treatment Not Found</h1>
          <p className="page-subtitle">Please browse treatments from the services section.</p>
          <Link to="/services" className="cta-button primary">View All Treatments</Link>
        </div>
      </section>
    );
  }

  const { category, service, subcategoryTitle, allServices } = serviceData;
  const summary = service.description.split('\n')[0];
  const pagePath = `/services/${category.id}/${serviceSlug}`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    serviceType: `${category.title} treatment`,
    description: summary,
    provider: {
      '@type': 'MedicalBusiness',
      name: 'ART Aesthetics & Skincare'
    },
    areaServed: SERVICE_AREA,
    url: `https://artaestheticsskincare.com${pagePath}`
  };

  const relatedServices = allServices
    .filter((entry) => entry.service.title !== service.title)
    .slice(0, 3);

  return (
    <section className="services category-page treatment-page">
      <SEO
        title={`${service.title} in Widnes | ART Aesthetics & Skincare`}
        description={`${service.title} at ART Aesthetics & Skincare in Widnes. ${summary}`}
        path={pagePath}
        jsonLd={schema}
      />

      <div className="services-container">
        <div className="treatment-breadcrumbs">
          <Link to="/services">Treatments</Link>
          <span>/</span>
          <Link to={`/services/${category.id}`}>{category.title}</Link>
          <span>/</span>
          <span>{service.title}</span>
        </div>

        <motion.article
          className="service-card service-treatment-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1>{service.title}</h1>
          {subcategoryTitle && <p className="service-subtitle">Category area: {subcategoryTitle}</p>}
          {service.subtitle && <p className="service-subtitle">{service.subtitle}</p>}
          <p className="service-description">{service.description}</p>

          {service.features && service.features.length > 0 && (
            <>
              <p className="features-label">Suitable for:</p>
              <ul className="features-list">
                {service.features.map((feature) => (
                  <li key={feature}>✨ {feature}</li>
                ))}
              </ul>
            </>
          )}

          {service.note && <p className="service-note">{service.note}</p>}

          <div className="service-treatment-links">
            <a
              href="https://that-time.co.uk/art-aesthetics-skincare"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button primary"
            >
              Book This Treatment
            </a>
            <Link to="/contact" className="cta-button secondary">Ask a Question First</Link>
          </div>
        </motion.article>

        <div className="category-internal-links">
          <h2>Related Treatments</h2>
          <div className="category-links-grid">
            {relatedServices.map((entry) => (
              <Link
                key={entry.serviceSlug}
                to={`/services/${category.id}/${entry.serviceSlug}`}
              >
                {entry.service.title}
              </Link>
            ))}
          </div>
          <p className="category-mini-links">
            <Link to={`/services/${category.id}`}>Back to {category.title}</Link> ·{' '}
            <Link to="/services">All treatment categories</Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServiceTreatment;